import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import Logo from "@/components/Logo";
import OtpVerify from "@/components/OtpVerify";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finish = (data, welcome) => {
    login(data.token, data.user);
    toast.success(welcome);
    nav(data.user.role === "admin" ? "/admin" : "/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        finish(data, "Welcome back!");
      } else {
        const { data } = await api.post("/auth/register", form);
        setOtp(data);
        toast.success("Verification code sent to your email");
      }
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12">
      <div className="w-full max-w-md fade-in">
        <div className="text-center mb-8"><div className="inline-flex"><Logo /></div></div>
        <div className="rounded-3xl border border-border surface-2 p-8">
          <h1 className="text-3xl font-display font-bold text-center">{isLogin ? "Welcome back" : "Create your account"}</h1>
          <p className="text-muted-foreground text-center mt-2 text-sm">{isLogin ? "Sign in to continue reading" : "Start reading stories you'll fall for"}</p>

          {otp ? <OtpVerify email={otp.email} initialCooldown={otp.resend_in} expiresIn={otp.expires_in} onBack={() => setOtp(null)}
            onVerified={(data) => finish(data, "Welcome to Fiction & Filters!")} /> : <>
          <form onSubmit={submit} className="space-y-4 mt-8">
            {!isLogin && <input required placeholder="Full name" value={form.name} onChange={set("name")} data-testid="auth-name" className="w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary" />}
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="auth-email" className="w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary" />
            {!isLogin && <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="auth-phone" className="w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary" />}
            <input required type="password" placeholder="Password" value={form.password} onChange={set("password")} data-testid="auth-password" className="w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary" />
            {isLogin && <div className="text-right -mt-2"><Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>}
            <button disabled={loading} data-testid="auth-submit" className="w-full px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? "Please wait…" : isLogin ? "Sign In" : "Sign Up"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" /></div>
          <button onClick={google} data-testid="google-login" className="w-full px-6 py-3 rounded-xl border border-border font-medium hover:border-primary transition-colors flex items-center justify-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <p className="text-center text-sm text-muted-foreground mt-6">
            {isLogin ? "New to F&F? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary font-medium">{isLogin ? "Sign up" : "Sign in"}</Link>
          </p>
          </>}
        </div>
      </div>
    </div>
  );
}
